<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :show-close="true"
    :close-on-click-modal="true"
    :modal-append-to-body="true"
    custom-class="register-dialog"
    width="480px"
    @close="handleClose"
    @open="onOpen"
  >
    <div class="dialog-header">
      <h3 class="dialog-title">{{ $t('register.title') }}</h3>
    </div>
    <el-form ref="registerForm" :model="form" :rules="rules" class="register-form">
      <el-form-item prop="email">
        <el-input v-model="form.email" :placeholder="$t('register.emailPlaceholder')" autocomplete="off"></el-input>
      </el-form-item>
      <el-form-item class="code-form-item" prop="code">
        <div class="code-row">
          <el-input v-model="form.code" :placeholder="$t('register.codePlaceholder')" autocomplete="off"></el-input>
          <span class="get-code-btn" @click="handleGetCode">{{ countdown > 0 ? `${countdown}s` : $t('register.getCode') }}</span>
        </div>
      </el-form-item>
      <el-form-item prop="password">
        <el-input v-model="form.password" type="password" :placeholder="$t('register.passwordPlaceholder')" autocomplete="new-password"></el-input>
      </el-form-item>
      <el-form-item prop="confirmPassword">
        <el-input v-model="form.confirmPassword" type="password" :placeholder="$t('register.confirmPlaceholder')" autocomplete="new-password"></el-input>
      </el-form-item>
      <el-form-item prop="nickname">
        <el-input v-model="form.nickname" :placeholder="$t('validation.nicknamePlaceholder')" autocomplete="off"></el-input>
      </el-form-item>
      <el-form-item prop="avatar" class="avatar-form-item">
        <div class="avatar-upload">
          <div class="avatar-box" v-loading="avatarUploading" @click="handleUploadAvatar">
            <img v-if="avatarPreview" :src="avatarPreview" alt="avatar" />
            <i v-else class="el-icon-plus"></i>
          </div>
          <span class="avatar-tip">{{ avatarUploading ? $t('register.uploading') : $t('validation.avatarTip') }}</span>
        </div>
        <input ref="avatarInput" class="avatar-input" type="file" accept="image/*" @change="handleAvatarChange" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" class="submit-btn" round @click="handleRegister">{{ $t('register.submit') }}</el-button>
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script>
import debounce from 'lodash/debounce'
import { getSignUrl } from '@/api/base'
import { uploadImageToOss } from '@/utils/oss'

export default {
  name: 'RegisterDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        email: '',
        code: '',
        password: '',
        confirmPassword: '',
        nickname: '',
        avatar: ''
      },
      codeTimer: null,
      countdown: 0,
      avatarPreview: '',
      avatarUploading: false
    }
  },
  computed: {
    rules() {
      return {
        email: [
          { required: true, message: this.$t('validation.emailRequired'), trigger: 'blur' },
          {
            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: this.$t('validation.emailFormat'),
            trigger: 'blur'
          }
        ],
        code: [
          { required: true, message: this.$t('validation.captchaRequired'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('validation.passwordRequired'), trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: this.$t('validation.confirmPasswordRequired'), trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value && value !== this.form.password) {
                callback(new Error(this.$t('forgot.noMatch')))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        nickname: [
          { required: true, message: this.$t('validation.nicknameRequired'), trigger: 'blur' }
        ],
        avatar: [
          { required: true, message: this.$t('validation.avatarRequired'), trigger: 'change' }
        ]
      }
    },
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  created() {
    this.handleRegister = debounce(this.submitRegister, 500, {
      leading: true,
      trailing: false
    })
  },
  methods: {
    onOpen() {
      this.resetForm()
    },
    resetForm() {
      if (this.codeTimer) {
        clearInterval(this.codeTimer)
        this.codeTimer = null
      }
      this.form = {
        email: '',
        code: '',
        password: '',
        confirmPassword: '',
        nickname: '',
        avatar: ''
      }
      this.countdown = 0
      this.avatarPreview = ''
      this.avatarUploading = false
      this.$nextTick(() => {
        this.$refs.registerForm && this.$refs.registerForm.clearValidate()
      })
    },
    handleClose() {
      if (this.$refs.registerForm) {
        this.$refs.registerForm.clearValidate()
      }
      this.$emit('update:visible', false)
    },
    handleGetCode() {
      if (this.countdown > 0) return
      this.$refs.registerForm.validateField('email', (errorMessage) => {
        if (errorMessage) return
        this.$emit('get-code', this.form.email)
        this.countdown = 60
        this.codeTimer = setInterval(() => {
          this.countdown--
          if (this.countdown <= 0) {
            clearInterval(this.codeTimer)
          }
        }, 1000)
      })
    },
    submitRegister() {
      if (this.avatarUploading) {
        return
      }
      this.$refs.registerForm.validate((valid) => {
        if (!valid) return
        this.$emit('register', this.form)
      })
    },
    handleUploadAvatar() {
      if (this.avatarUploading) return
      this.$refs.avatarInput && this.$refs.avatarInput.click()
    },
    async handleAvatarChange(event) {
      const file = (event.target.files || [])[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        // this.$message.warning('请选择图片文件')
        event.target.value = ''
        return
      }
      // if (file.size > 1024 * 1024 * 5) {
      //   this.$message.warning('头像图片不能超过 5MB')
      //   event.target.value = ''
      //   return
      // }

      this.avatarUploading = true
      try {
        const result = await uploadImageToOss(file)
        const signResult = await getSignUrl(result.url)
        this.form.avatar = result.url
        this.avatarPreview = signResult.data || result.url
        this.$nextTick(() => {
          this.$refs.registerForm && this.$refs.registerForm.validateField('avatar')
        })
      } catch (error) {
        console.error(error)
        this.$message.error(error.message)
      } finally {
        this.avatarUploading = false
        event.target.value = ''
      }
    }
  },
  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer)
    }
    if (this.handleRegister && typeof this.handleRegister.cancel === 'function') {
      this.handleRegister.cancel()
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-header {
  text-align: center;
  margin-bottom: 30px;

  .dialog-title {
    font-size: 20px;
    font-weight: 600;
    color: #0866FF;
  }
}

.register-form {
  padding: 0 20px;

  ::v-deep .el-input__inner {
    border: none;
    border-radius: 25px;
    background: #F8F8F8;
    padding-left: 16px;
    font-size: 14px;
    height: 44px;

    &:focus {
      border: none;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px #F8F8F8 inset;
      transition: background-color 5000s ease-in-out 0s;
    }
  }
}

.code-form-item {
  ::v-deep .el-input__inner {
    background: transparent;
    border-radius: 0;
  }
}

.code-row {
  display: flex;
  align-items: center;
  background: #F8F8F8;
  border-radius: 25px;
  padding-right: 16px;

  .el-input {
    flex: 1;
  }

  .get-code-btn {
    margin-left: 12px;
    font-size: 14px;
    color: #0866FF;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      opacity: 0.8;
    }
  }
}

.avatar-form-item {
  ::v-deep .el-form-item__error {
    left: 0;
    right: 0;
    text-align: center;
  }
}

.avatar-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;

  .avatar-box {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    border: 1px dashed #ccc;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    i {
      font-size: 24px;
      color: #ccc;
    }
  }

  .avatar-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #999;
  }
}

.avatar-input {
  display: none;
}

.submit-btn {
  width: 100%;
  background: #0866FF;
  border-color: #0866FF;
  font-size: 16px;
  padding: 12px 0;
}
</style>

<style lang="scss">
.register-dialog {
  border-radius: 25px !important;
  padding: 30px 40px;

  .el-dialog__header {
    padding: 0;
    position: relative;
  }

  .el-dialog__headerbtn {
    top: 0;
    right: 0;
    font-size: 20px;
    display: block;
    position: absolute;
    z-index: 1;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>
